/**
 * Per-scene particle budgeting (tech-design §4, §8: "particle budget per scene …
 * effects degrade first (settings tier)").
 *
 * The pure accounting — how many particles a scene may have alive at once given
 * the player's quality tier, and admission control on spawn — lives here so it
 * unit-tests headlessly. The actual Pixi sprites are pooled and driven in the
 * scene; this module never imports Pixi.
 */

/** Player-selectable particle quality (settings tier). */
export type ParticleTier = 'off' | 'low' | 'medium' | 'high';

/** Fraction of a scene's base budget granted at each tier. */
const TIER_SCALE: Record<ParticleTier, number> = {
  off: 0,
  low: 0.25,
  medium: 0.6,
  high: 1,
};

export const PARTICLE_TIERS: readonly ParticleTier[] = ['off', 'low', 'medium', 'high'];

/**
 * Tracks how many particles a scene may spawn. `baseBudget` is the scene's
 * high-tier ceiling; the active tier scales it down. `requestBurst` returns how
 * many of a desired count fit in the remaining budget (never over-commits), and
 * the caller reports completions via {@link release}.
 */
export class ParticleBudget {
  private readonly baseBudget: number;
  private tier: ParticleTier;
  private alive = 0;

  constructor(baseBudget: number, tier: ParticleTier = 'high') {
    if (!Number.isInteger(baseBudget) || baseBudget < 0) {
      throw new RangeError(
        `baseBudget must be a non-negative integer, got ${String(baseBudget)}`,
      );
    }
    this.baseBudget = baseBudget;
    this.tier = tier;
  }

  /** Maximum particles allowed alive at the current tier. */
  get capacity(): number {
    return Math.floor(this.baseBudget * TIER_SCALE[this.tier]);
  }

  /** Particles currently alive. */
  get active(): number {
    return this.alive;
  }

  /** Remaining headroom before the cap. */
  get available(): number {
    return Math.max(0, this.capacity - this.alive);
  }

  /** Switch the quality tier; excess live particles are trimmed to the new cap. */
  setTier(tier: ParticleTier): void {
    this.tier = tier;
    if (this.alive > this.capacity) {
      this.alive = this.capacity;
    }
  }

  /**
   * Reserve up to `desired` particles for a burst, returning how many were
   * actually granted (0 when the budget is full or the tier is `off`). The
   * caller spawns exactly that many and calls {@link release} as they expire.
   */
  requestBurst(desired: number): number {
    if (desired <= 0) {
      return 0;
    }
    const granted = Math.min(desired, this.available);
    this.alive += granted;
    return granted;
  }

  /** Report that `count` particles have expired, freeing their budget. */
  release(count = 1): void {
    this.alive = Math.max(0, this.alive - count);
  }

  /** Reset the live count (e.g. on scene teardown). */
  clear(): void {
    this.alive = 0;
  }
}
